import * as React from 'react';
import { cn } from '@/lib/utils';

const inputPatterns = {
  lettersOnly: /^[A-Za-z]+$/,
  lettersWithSpace: /^[A-Za-z\s]+$/,
  numbersOnly: /^[0-9]+$/,
  alphanumeric: /^[A-Za-z0-9]+$/,
  email: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
  phone: /^\+?[\d\s-()]{8,}$/,
  password: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
  username: /^[A-Za-z][A-Za-z0-9_]{3,29}$/,
  date: /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
  time: /^([01]\d|2[0-3]):([0-5]\d)$/,
  gst: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
} as const;

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  register?: any;
  name?: string;
  options?: string[];
  required?: boolean;
  patterns?: {
    lettersOnly?: boolean;
    lettersWithSpace?: boolean;
    numbersOnly?: boolean;
    alphanumeric?: boolean;
    email?: boolean;
    phone?: boolean;
    password?: boolean;
    username?: boolean;
    date?: boolean;
    time?: boolean;
    custom?: RegExp;
  };
  minLength?: number;
  maxLength?: number;
  custom?: (value: string) => boolean;
  validators?: keyof typeof inputPatterns;
  onValidationError?: (errors: string[]) => void;
  error?: string;
  prefix?: React.ReactNode;
  tabIndex?: number;
  onTab?: (direction: 'forward' | 'backward') => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      id,
      width = 'w-full',
      options,
      validators,
      error,
      'aria-errormessage': ariaErrorMessage,
      register,
      name,
      prefix,
      onChange,
      onTab,
      ...props
    },
    ref,
  ) => {
    const patternDescriptions = {
      lettersOnly: 'letters only (A-Z, a-z)',
      lettersWithSpace: 'letters and spaces',
      numbersOnly: 'numbers only',
      alphanumeric: 'letters and numbers',
      email: 'valid email address',
      phone: 'valid phone number',
      password:
        'minimum 8 characters, at least one letter, one number, and one special character',
      username:
        'start with a letter, 4-30 characters, letters, numbers, and underscores only',
      date: 'YYYY-MM-DD format',
      time: 'HH:MM format (24-hour)',
      gst: 'a valid GST number (Format: 22AAAAA0000A1Z5)',
    };

    const getValidationRules = (validator: keyof typeof inputPatterns) => ({
      pattern: {
        value: inputPatterns[validator],
        message: `Please enter ${patternDescriptions[validator]}`,
      },
    });

    const getRegisterProps = () => {
      if (!register || !name) return {};
      return register(name, validators ? getValidationRules(validators) : {});
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (onTab) {
          e.preventDefault();
          onTab(e.shiftKey ? 'backward' : 'forward');
        }
      }
    };

    return (
      <div className="relative">
        {options ? (
          <select
            className={cn(
              'placeholder:text-muted-foreground focus-visible:ring-ring border-border-light flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50',
              className,
              width,
            )}
            {...getRegisterProps()}
            ref={ref as React.Ref<HTMLSelectElement>}
            onChange={
              onChange as unknown as React.ChangeEventHandler<HTMLSelectElement>
            }
            onKeyDown={handleKeyDown}
            {...props}
          >
            {options.map((option, id) => (
              <option key={id} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <>
            <div className="relative">
              {prefix && (
                <div className="absolute left-0 top-0 flex h-full items-center">
                  {prefix}
                </div>
              )}
              <input
                id={id}
                type={type}
                className={cn(
                  'border-input placeholder:text-muted-foreground focus-visible:ring-ring border-bdr-default flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                  error && 'border-red-500',
                  className,
                  width,
                )}
                aria-invalid={error ? 'true' : 'false'}
                aria-errormessage={ariaErrorMessage}
                {...getRegisterProps()}
                ref={ref}
                onKeyDown={handleKeyDown}
                {...props}
              />
            </div>
            {error && (
              <p className="mt-1 text-xs text-red-500" id={ariaErrorMessage}>
                {error}
              </p>
            )}
          </>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
